import React, { useState } from 'react';
import styled from 'styled-components';
import { Play, Github, ExternalLink, Copy, Check } from 'lucide-react';

const PageContainer = styled.div`
  padding-top: 64px;
  min-height: calc(100vh - 64px);
  background: ${props => props.theme.colors.background};
`;

// Top navigation for docs sections (instead of sidebar)
const DocsHeader = styled.header`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
`;

const DocsHeaderInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${props => props.theme.spacing[4]};
`;

const DocsTitleBlock = styled.div`
  min-width: 240px;
`;

const DocsTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['2xl']};
  margin: 0 0 ${props => props.theme.spacing[1]} 0;
`;

const DocsSubtitle = styled.p`
  margin: 0;
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const DocsTabs = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[2]};
`;

const DocsTab = styled.button`
  border: 1px solid ${props => (props.active ? props.theme.colors.primary : props.theme.colors.border)};
  background: ${props => (props.active ? props.theme.colors.primary : props.theme.colors.surface)};
  color: ${props => (props.active ? props.theme.colors.text.inverse : props.theme.colors.text.secondary)};
  border-radius: ${props => props.theme.borderRadius.full};
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  font-size: ${props => props.theme.fontSizes.sm};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.active ? props.theme.colors.primary : props.theme.colors.background)};
  }
`;

const MainContent = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing[8]} ${props => props.theme.spacing[6]};
`;

const Section = styled.section`
  margin-bottom: ${props => props.theme.spacing[12]};
  scroll-margin-top: 80px;
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes['3xl']};
  margin-bottom: ${props => props.theme.spacing[6]};
  color: ${props => props.theme.colors.text.primary};
  border-bottom: 2px solid ${props => props.theme.colors.border};
  padding-bottom: ${props => props.theme.spacing[3]};
`;

const SectionSubtitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.xl};
  margin: ${props => props.theme.spacing[6]} 0 ${props => props.theme.spacing[4]};
  color: ${props => props.theme.colors.text.primary};
`;

const CodeBlockContainer = styled.div`
  position: relative;
  margin: ${props => props.theme.spacing[4]} 0;
`;

const CodeBlock = styled.pre`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing[4]};
  overflow-x: auto;
  font-family: ${props => props.theme.fonts.mono};
  font-size: ${props => props.theme.fontSizes.sm};
  margin: 0;
  color: ${props => props.theme.colors.text.primary};
`;

const CopyButton = styled.button`
  position: absolute;
  top: ${props => props.theme.spacing[2]};
  right: ${props => props.theme.spacing[2]};
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[2]};
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  font-size: ${props => props.theme.fontSizes.xs};

  &:hover {
    background: ${props => props.theme.colors.surface};
    color: ${props => props.theme.colors.text.primary};
  }
`;

const InlineCode = styled.code`
  background: ${props => props.theme.colors.surface};
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[2]};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-family: ${props => props.theme.fonts.mono};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const InfoBox = styled.div`
  background: ${props => props.background || props.theme.colors.surface};
  border: 1px solid ${props => props.border || props.theme.colors.border};
  border-left: 4px solid ${props => props.accent || props.theme.colors.accent};
  padding: ${props => props.theme.spacing[4]};
  border-radius: ${props => props.theme.borderRadius.md};
  margin: ${props => props.theme.spacing[4]} 0;
`;

const InfoBoxTitle = styled.h4`
  margin: 0 0 ${props => props.theme.spacing[2]} 0;
  color: ${props => props.theme.colors.text.primary};
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const InfoBoxContent = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: ${props => props.theme.spacing[4]} 0;
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${props => props.theme.colors.border};
`;

const TableHeader = styled.th`
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  text-align: left;
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.text.primary};
`;

const TableCell = styled.td`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text.secondary};
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.text.inverse};
  text-decoration: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.accent};
    transform: translateY(-1px);
  }
`;

const CopyableCodeBlock = ({ children, language = 'text' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <CodeBlockContainer>
      <CodeBlock>{children}</CodeBlock>
      <CopyButton onClick={handleCopy}>
        {copied ? (
          <>
            <Check size={12} />
            Copied!
          </>
        ) : (
          <>
            <Copy size={12} />
            Copy
          </>
        )}
      </CopyButton>
    </CodeBlockContainer>
  );
};

const DocumentationPage = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <PageContainer>
      <DocsHeader>
        <DocsHeaderInner>
          <DocsTitleBlock>
            <DocsTitle>Malaria Detection Documentation</DocsTitle>
            <DocsSubtitle>Model training, API usage, and deployment for the malaria detection system.</DocsSubtitle>
          </DocsTitleBlock>
          <DocsTabs>
            <DocsTab
              active={activeSection === 'getting-started'}
              onClick={() => scrollToSection('getting-started')}
            >
              Overview
            </DocsTab>
            <DocsTab
              active={activeSection === 'installation'}
              onClick={() => scrollToSection('installation')}
            >
              Setup
            </DocsTab>
            <DocsTab
              active={activeSection === 'api-reference'}
              onClick={() => scrollToSection('api-reference')}
            >
              API
            </DocsTab>
            <DocsTab
              active={activeSection === 'examples'}
              onClick={() => scrollToSection('examples')}
            >
              Examples
            </DocsTab>
            <DocsTab
              active={activeSection === 'deployment'}
              onClick={() => scrollToSection('deployment')}
            >
              Deployment
            </DocsTab>
          </DocsTabs>
        </DocsHeaderInner>
      </DocsHeader>

      <MainContent>
        <Section id="getting-started">
          <SectionTitle>Getting Started</SectionTitle>
          <p>
            Welcome to the Malaria Detection documentation. This guide will help you
            understand how to use our deep learning model for malaria parasite detection in blood smear images.
          </p>

          <InfoBox>
            <InfoBoxTitle>Live Links</InfoBoxTitle>
            <InfoBoxContent>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                <div>
                  <strong>Frontend demo:</strong> <InlineCode>https://deep-learning-malaria-detection.vercel.app</InlineCode>
                </div>
                <div>
                  <strong>Backend API:</strong> <InlineCode>https://deep-learning-malaria-detection.onrender.com</InlineCode>
                </div>
              </div>
            </InfoBoxContent>
          </InfoBox>

          <InfoBox>
            <InfoBoxTitle>What is Malaria Detection?</InfoBoxTitle>
            <InfoBoxContent>
              Our system provides a clinical decision support tool for detecting malaria parasites
              in blood smear images using deep learning. It uses state-of-the-art CNN architectures
              (VGG16, ResNet-50, EfficientNet) with transfer learning. The best-performing model is EfficientNet-B0 with 97.32% accuracy on the NIH cell images dataset.
            </InfoBoxContent>
          </InfoBox>

          <SectionSubtitle>Key Features</SectionSubtitle>
          <ul>
            <li><strong>Transfer Learning:</strong> Pre-trained models (VGG16, ResNet-50, EfficientNet) fine-tuned for malaria detection</li>
            <li><strong>High Accuracy:</strong> EfficientNet achieves 97.32% accuracy (reported best model)</li>
            <li><strong>Explainable AI:</strong> Grad-CAM visualization highlights regions influencing predictions</li>
            <li><strong>Confidence Scores:</strong> Provides probability scores for each prediction</li>
            <li><strong>Production Ready:</strong> FastAPI backend with React frontend</li>
          </ul>
        </Section>

        <Section id="installation">
          <SectionTitle>Installation</SectionTitle>
          <p>
            Follow these steps to set up the Malaria Detection system in your environment.
          </p>

          <SectionSubtitle>Prerequisites</SectionSubtitle>
          <ul>
            <li>Python 3.8 or higher</li>
            <li>PyTorch 2.0+</li>
            <li>CUDA-compatible GPU (recommended for training)</li>
            <li>Node.js 16+ (for frontend)</li>
          </ul>

          <SectionSubtitle>Dataset</SectionSubtitle>
          <ul>
            <li><strong>Total images:</strong> 27,560 (13,780 Parasitized + 13,780 Uninfected)</li>
            <li><strong>Splits:</strong> 80% train / 10% validation / 10% test</li>
            <li><strong>Input size:</strong> 224×224 RGB (resized)</li>
          </ul>

          <SectionSubtitle>Backend Setup</SectionSubtitle>
          <CopyableCodeBlock language="bash">
            {`cd ml-model
pip install -r requirements.txt`}
          </CopyableCodeBlock>

          <SectionSubtitle>Frontend Setup</SectionSubtitle>
          <CopyableCodeBlock language="bash">
            {`cd demo-app
npm install`}
          </CopyableCodeBlock>

          <InfoBox>
            <InfoBoxTitle>Note</InfoBoxTitle>
            <InfoBoxContent>
              The main training notebook in this repository is <InlineCode>deep_learning_malaria_detection.ipynb</InlineCode> (also available under <InlineCode>ml-model/notebooks/deep_learning_malaria_detection.ipynb</InlineCode>).
              Training produces <InlineCode>ml-model/models/malaria_detection_model.pth</InlineCode>, which the API loads on startup.
            </InfoBoxContent>
          </InfoBox>
        </Section>

        <Section id="api-reference">
          <SectionTitle>API Reference</SectionTitle>
          <p>
            The FastAPI backend provides RESTful endpoints for malaria detection.
          </p>

          <InfoBox>
            <InfoBoxTitle>Deployed API</InfoBoxTitle>
            <InfoBoxContent>
              The public API is deployed on Render at <InlineCode>https://deep-learning-malaria-detection.onrender.com</InlineCode>.
            </InfoBoxContent>
          </InfoBox>

          <SectionSubtitle>Endpoints</SectionSubtitle>
          <Table>
            <thead>
              <tr>
                <TableHeader>Endpoint</TableHeader>
                <TableHeader>Method</TableHeader>
                <TableHeader>Description</TableHeader>
              </tr>
            </thead>
            <tbody>
              <tr>
                <TableCell><InlineCode>/health</InlineCode></TableCell>
                <TableCell>GET</TableCell>
                <TableCell>Health check endpoint</TableCell>
              </tr>
              <tr>
                <TableCell><InlineCode>/api/predict</InlineCode></TableCell>
                <TableCell>POST</TableCell>
                <TableCell>Predict malaria from uploaded image</TableCell>
              </tr>
              <tr>
                <TableCell><InlineCode>/api/predict-with-cam</InlineCode></TableCell>
                <TableCell>POST</TableCell>
                <TableCell>Predict with Grad-CAM visualization</TableCell>
              </tr>
              <tr>
                <TableCell><InlineCode>/api/load-model</InlineCode></TableCell>
                <TableCell>POST</TableCell>
                <TableCell>Load trained model</TableCell>
              </tr>
            </tbody>
          </Table>

          <SectionSubtitle>Request Format</SectionSubtitle>
          <p>
            Upload an image file using multipart/form-data:
          </p>
          <CopyableCodeBlock language="bash">
            {`curl -X POST "https://deep-learning-malaria-detection.onrender.com/api/predict" \\
  -F "file=@blood_smear_image.png"`}
          </CopyableCodeBlock>

          <SectionSubtitle>Response Format</SectionSubtitle>
          <CopyableCodeBlock language="json">
            {`{
  "prediction": "Parasitized",
  "class_index": 1,
  "confidence": 0.95,
  "probabilities": {
    "uninfected": 0.05,
    "parasitized": 0.95
  }
}`}
          </CopyableCodeBlock>
        </Section>

        <Section id="examples">
          <SectionTitle>Examples</SectionTitle>
          <p>
            Explore practical examples of using the Malaria Detection system.
          </p>

          <SectionSubtitle>Python Client Example</SectionSubtitle>
          <CopyableCodeBlock language="python">
            {`import requests

# API endpoint
url = "https://deep-learning-malaria-detection.onrender.com/api/predict"

# Upload image
with open('blood_smear.png', 'rb') as f:
    files = {'file': f}
    response = requests.post(url, files=files)
    result = response.json()
    
    print(f"Prediction: {result['prediction']}")
    print(f"Confidence: {result['confidence']:.2%}")
    print(f"Probabilities: {result['probabilities']}")`}
          </CopyableCodeBlock>

          <SectionSubtitle>JavaScript/React Example</SectionSubtitle>
          <CopyableCodeBlock language="javascript">
            {`import { predictMalaria } from './utils/api';

const handleImageUpload = async (imageFile) => {
  try {
    const result = await predictMalaria(imageFile, true);
    console.log('Prediction:', result.prediction);
    console.log('Confidence:', result.confidence);
    console.log('Grad-CAM:', result.gradcam_overlay);
  } catch (error) {
    console.error('Error:', error);
  }
};`}
          </CopyableCodeBlock>
        </Section>

        <Section id="deployment">
          <SectionTitle>Deployment</SectionTitle>
          <p>
            Deploy the Malaria Detection system to production environments.
          </p>

          <SectionSubtitle>Backend (Render)</SectionSubtitle>
          <p>
            The FastAPI inference service is deployed as a Docker web service on Render.
          </p>
          <CopyableCodeBlock language="text">
            {`API Base URL: https://deep-learning-malaria-detection.onrender.com
Health check:  GET /health
Predict:       POST /api/predict
Predict + CAM: POST /api/predict-with-cam`}
          </CopyableCodeBlock>

          <SectionSubtitle>Starting the Backend</SectionSubtitle>
          <CopyableCodeBlock language="bash">
            {`cd ml-model
python run_api.py

# Or with uvicorn directly
uvicorn src.api.main:app --reload --host 0.0.0.0 --port 8000`}
          </CopyableCodeBlock>

          <SectionSubtitle>Starting the Frontend</SectionSubtitle>
          <CopyableCodeBlock language="bash">
            {`cd demo-app
npm start`}
          </CopyableCodeBlock>

          <SectionSubtitle>Environment Variables</SectionSubtitle>
          <CopyableCodeBlock language="bash">
            {`# Backend
MODEL_PATH=./models/malaria_detection_model.pth
MODEL_TYPE=efficientnet

# Frontend
REACT_APP_API_URL=https://deep-learning-malaria-detection.onrender.com`}
          </CopyableCodeBlock>

          <SectionSubtitle>Production Considerations</SectionSubtitle>
          <ul>
            <li><strong>Model Loading:</strong> Load the model once at startup for better performance</li>
            <li><strong>Image Preprocessing:</strong> Ensure images are properly resized and normalized</li>
            <li><strong>Error Handling:</strong> Implement proper error handling for invalid images</li>
            <li><strong>Security:</strong> Add authentication and rate limiting for production</li>
            <li><strong>Monitoring:</strong> Implement logging and metrics collection</li>
          </ul>
        </Section>

        <Section>
          <SectionTitle>Next Steps</SectionTitle>
          <p>
            Now that you have a basic understanding, here are some next steps:
          </p>
          <ul>
            <li>Try the live demo to see the system in action</li>
            <li>Train your own model using the Jupyter notebook</li>
            <li>Explore the API endpoints and integrate into your applications</li>
            <li>Review the model architecture and training pipeline</li>
          </ul>

          <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Button href="/demo">
              <Play size={16} />
              Try Demo
            </Button>
            <Button href="https://github.com/Johannes613/deep-learning-malaria-detection" target="_blank">
              <Github size={16} />
              View on GitHub
            </Button>
          </div>
        </Section>
      </MainContent>
    </PageContainer>
  );
};

export default DocumentationPage;

